import React, { useState, useEffect, useRef } from "react";
import api from "../../api";
import swalAlert from "../../utils/swalAlert";
import useToastify from "../../hooks/useToastify";
import useSweetAlert from "../../hooks/useSweetAlert";
import { buildColorsListPath } from "../../utils/color-api";
import { parseVisibleTinterWorkbook } from "../../utils/tinter-import";

// หลักการทำงาน: สร้าง HTML summary จากผล preview ของ backend โดยไม่ให้หน้าเว็บคำนวณ duplicate เอง
// backend เป็น source of truth ของ new/update/normalized duplicate ส่วน frontend แค่แสดงผลให้ผู้ใช้ยืนยัน
const buildTinterImportPreviewHtml = (importResult, preview = {}) => {
  const count = (value) => Number(value || 0);
  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  const visibleSheets = importResult.visibleSheetNames.map(escapeHtml).join(", ");
  const dbDuplicateConflicts = preview.db_duplicate_conflicts || [];
  const dbDuplicatePreview = dbDuplicateConflicts
    .slice(0, 3)
    .map((item) => {
      const existingCodes = (item.existing_codes || []).map(escapeHtml).join(", ");
      return `${escapeHtml(item.normalized_code)} (${existingCodes})`;
    })
    .join("<br />");

  return `
    <div style="text-align: left">
      <div>Visible sheets: ${visibleSheets}</div>
      <div>Valid rows: ${count(preview.valid_count || importResult.colors.length)}</div>
      <div>Will write: ${count(preview.imported_count || preview.operation_count)}</div>
      <div>New rows: ${count(preview.new_count)}</div>
      <div>Update same code: ${count(preview.exact_update_count)}</div>
      <div>Update same tinter: ${count(preview.normalized_update_count)}</div>
      <div>Skipped rows: ${count(importResult.skippedRows.length + count(preview.skipped_count))}</div>
      <div>Sheet errors: ${count(importResult.errors.length)}</div>
      <div>Duplicate in file: ${count(preview.incoming_duplicate_count || preview.duplicate_count)}</div>
      <div>Duplicate already in DB: ${count(preview.db_duplicate_count)}</div>
      ${
        dbDuplicatePreview
          ? `<div style="margin-top: 8px; color: #b45309">DB duplicate groups:<br />${dbDuplicatePreview}</div>`
          : ""
      }
    </div>
  `;
};

const ColorSystemCode = ({
  name = "",
  code = "",
  weight = "",
  milliliter = "",
  Thainame,
  deleteIngradient,
  id,
}) => {
  return (
    <div className="px-4 py-2 bg-white rounded-lg items-start my-4 ">
      <div>
        <div className="flex justify-between my-4">
          <div className="grid grid-cols-1 w-full ">
            <div>Tinter Code: {code}</div>
          </div>
          <svg
            onClick={() => deleteIngradient(id)}
            className="w-6 h-6 text-textred cursor-pointer "
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-4">
          <div>Tinter Name: {name}</div>
          <div>Thai Name : {Thainame}</div>
          {/* <div>Weight (grams): {weight} </div> */}
          <div>SG density: {milliliter} </div>
        </div>
      </div>
    </div>
  );
};

const TinterCode = ({ index }) => {
  const importFileInputRef = useRef(null);
  const [currentSystemCodeForInputCode, setCurrentSystemCodeForInputCode] =
    useState("");
  const [currentSystemCodeForInputName, setCurrentSystemCodeForInputName] =
    useState("");
  const [
    currentSystemCodeForInputMilliliter,
    setCurrentSystemCodeForInputMilliliter,
  ] = useState("");

  const [currentSystemCodeForNameTH, setCurrentSystemCodeForNameTH] =
    useState("");

  const [systemCodes, setSystemCodes] = useState([]);

  const [, setLoading] = useState("");
  const [importing, setImporting] = useState(false);
  const { notifySuccess, notifyError } = useToastify();
  const { confirm } = useSweetAlert();

  useEffect(() => {
    LoadColors();
  }, []);

  // หลักการทำงาน: โหลด master tinter ในหน้า Settings ด้วย limit เดียวกับ dropdown เพื่อไม่หลุดรายการท้าย collection
  const LoadColors = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(buildColorsListPath());
      const data_table = data?.colors?.map((row) => {
        return {
          code: row.color_code,
          name: row.color_name,
          name_th: row.color_name_th,
          grams: row.weight,
          milliliter: row.density,
          id: row._id,
        };
      });
      setSystemCodes(data_table);
    } catch (error) {
      console.error("ERROR TO LOAD COLORS :", error);
    } finally {
      setLoading(false);
    }
  };
  /// API
  const CreateTinterCode = async () => {
    if (
      currentSystemCodeForInputCode &&
      currentSystemCodeForInputName &&
      currentSystemCodeForNameTH &&
      // currentSystemCodeForInputWeight &&
      currentSystemCodeForInputMilliliter
    ) {
      setLoading(true);
      try {
        await api.post(`/colors`, {
          color_code: currentSystemCodeForInputCode,
          color_name: currentSystemCodeForInputName,
          color_name_th: currentSystemCodeForNameTH,
          // weight: Number(currentSystemCodeForInputWeight),
          density: Number(currentSystemCodeForInputMilliliter),
        });
        //ResetState
        setCurrentSystemCodeForInputCode("");
        setCurrentSystemCodeForInputName("");
        // setCurrentSystemCodeForInputWeight("");
        setCurrentSystemCodeForInputMilliliter("");
        setCurrentSystemCodeForNameTH("");
        LoadColors();
        notifySuccess();
      } catch (error) {
        console.error("ERROR TO CREAT TINTER :", error);
      } finally {
        setLoading(false);
      }
    } else {
      await swalAlert.Fail({ text: "Please fill in all the required information." });
    }
  };

  const clickDelete = async (id) => {
    await confirm({
      title: "Do you want to delete data?" ,
      callback: () => deleteIngradient(id),
    });
  };

  const deleteIngradient = async (id) => {
    await api.delete(`/colors/${id}`);
    LoadColors();
    notifySuccess();

    // };

    // let temp = [...systemCodes];
    // if (index > -1) {
    //   temp.splice(index, 1);
    // }
    // setSystemCodes(temp);
  };

  // หลักการทำงาน: ส่งรายการ tinter ที่ parse แล้วไป backend bulk import
  // หลังสำเร็จจะ reload master list เพื่อให้หน้า Settings แสดง density ล่าสุดทันที
  const submitTinterImport = async (colors = []) => {
    const { data } = await api.post(`/colors/import`, { colors });
    await LoadColors();
    notifySuccess(`Imported ${data?.imported_count || colors.length} tinter codes`);
    return data;
  };

  // หลักการทำงาน: ให้ backend วิเคราะห์ผล import ก่อนเขียน DB
  // ใช้เพื่อกัน frontend กับ backend ตีความ duplicate ไม่ตรงกัน โดยเฉพาะ code ที่ต่างกันแค่ขีดหรือช่องว่าง
  const previewTinterImport = async (colors = []) => {
    const { data } = await api.post(`/colors/import/preview`, { colors });
    return data;
  };

  // หลักการทำงาน: อ่านไฟล์ Excel จาก input แล้วให้ helper ดึงเฉพาะชีต visible
  // จากนั้นสรุปจำนวนแถวก่อน confirm เพื่อกันการ import จากชีต hidden หรือไฟล์ผิดรูปแบบโดยไม่ตั้งใจ
  const onChangeTinterImportFile = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setImporting(true);

    try {
      const importResult = await parseVisibleTinterWorkbook(file);

      if (importResult.colors.length === 0) {
        await swalAlert.Fail({
          title: "Import failed",
          text:
            importResult.errors[0] ||
            "No valid tinter rows found in visible sheets.",
        });
        return;
      }

      const preview = await previewTinterImport(importResult.colors);

      await confirm({
        title: "Import Tinter Code?",
        callback: async () => submitTinterImport(importResult.colors),
        other: {
          html: buildTinterImportPreviewHtml(importResult, preview),
        },
      });
    } catch (error) {
      console.error("ERROR TO IMPORT TINTER :", error);
      notifyError("Import failed");
      await swalAlert.Fail({
        title: "Import failed",
        text: error?.message || "Cannot import this Excel file.",
      });
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mt-10">
        <div className="text-xl">Tinter Code</div>
        <div
          className={`flex justify-center items-center space-x-2 rounded-lg p-2 text-white bg-waitingbuttoncolor ${
            importing ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
          }`}
          onClick={() => {
            if (!importing) {
              importFileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={importFileInputRef}
            className="hidden"
            type="file"
            accept=".xlsx,.xls"
            onChange={onChangeTinterImportFile}
          />
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          <div>{importing ? "Importing..." : "Import Excel"}</div>
        </div>
      </div>
      <div className="mt-10">
        <div className="grid grid-cols-1 my-2">
          <div>Tinter Code</div>
          <div className="flex justify-start items-center space-x-4 mt-2">
            <input
              className="w-full  focus:outline-none bg-bgseccion   px-4 py-2 rounded-lg"
              placeholder="  AE10 "
              value={currentSystemCodeForInputCode}
              onChange={(e) => setCurrentSystemCodeForInputCode(e.target.value)}
            />
          </div>
        </div>
        <div className=" grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="">
            <div>Tinter Name</div>
            <div className="flex justify-start items-center space-x-4 mt-2">
              <input
                className="w-full  focus:outline-none bg-bgseccion   px-4 py-2 rounded-lg"
                placeholder="  MASTER COLOUR COARSEALU"
                value={currentSystemCodeForInputName}
                onChange={(e) =>
                  setCurrentSystemCodeForInputName(e.target.value)
                }
              />
            </div>
          </div>
          <div className="">
            <div>Thai Name</div>
            <div className="flex justify-start items-center space-x-4 mt-2">
              <input
                className="w-full  focus:outline-none bg-bgseccion   px-4 py-2 rounded-lg"
                placeholder="Thai Name"
                value={currentSystemCodeForNameTH}
                onChange={(e) => setCurrentSystemCodeForNameTH(e.target.value)}
              />
            </div>
          </div>
          {/* <div className="">
            <div>Weight (grams)</div>
            <div className="flex justify-start items-center space-x-4 mt-2">
              <input
                className="w-full  focus:outline-none bg-bgseccion   px-4 py-2 rounded-lg"
                placeholder="10 grams"
                value={currentSystemCodeForInputWeight}
                onChange={(e) =>
                  setCurrentSystemCodeForInputWeight(e.target.value)
                }
              />
            </div>
          </div> */}
          <div className="">
            <div>SG density</div>
            <div className="flex justify-start items-center space-x-4 mt-2">
              <input
                className="w-full  focus:outline-none bg-bgseccion   px-4 py-2 rounded-lg"
                placeholder="10 grams"
                value={currentSystemCodeForInputMilliliter}
                onChange={(e) =>
                  setCurrentSystemCodeForInputMilliliter(e.target.value)
                }
                type="number"
              />
            </div>
          </div>
        </div>
        <div
          className="flex justify-center items-center space-x-2  mt-4 w-full ml-auto  md:w-40 rounded-lg p-2 cursor-pointer text-white bg-waitingbuttoncolor"
          onClick={() => CreateTinterCode()}
        >
          <svg
            className="w-6 h-6  "
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <div>Add</div>
        </div>
      </div>
      <div className=" bg-bgseccion rounded-lg w-full p-4  h-card md:h-bill overflow-y-scroll mt-6  ">
        {systemCodes.map((systemCode, index) => {
          return (
            <div key={index}>
              <ColorSystemCode
                code={systemCode.code}
                name={systemCode.name}
                Thainame={systemCode.name_th}
                weight={systemCode.grams}
                milliliter={systemCode.milliliter}
                index={index}
                id={systemCode.id}
                deleteIngradient={clickDelete}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TinterCode;
